import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import type { Database } from "@/integrations/supabase/types";

type PriceBook = Database["public"]["Tables"]["price_books"]["Row"];
type ProductPrice = Database["public"]["Tables"]["product_prices"]["Row"];
type ProductPriceInsert = Database["public"]["Tables"]["product_prices"]["Insert"];

export const usePriceBooks = () => {
  const queryClient = useQueryClient();

  const { data: priceBooks, isLoading: priceBooksLoading } = useQuery({
    queryKey: ["price_books"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("price_books")
        .select("*")
        .order("county", { ascending: true });
      
      if (error) throw error;
      return data as PriceBook[];
    },
  });

  const { data: productPrices, isLoading: pricesLoading } = useQuery({
    queryKey: ["product_prices"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("product_prices")
        .select(`
          *,
          product:products(*),
          price_book:price_books(*)
        `);
      
      if (error) throw error;
      return data as (ProductPrice & {
        product: Database["public"]["Tables"]["products"]["Row"];
        price_book: PriceBook;
      })[];
    },
  });

  const updatePrice = useMutation({
    mutationFn: async ({ 
      product_id, 
      price_book_id, 
      unit_price, 
      cost, 
      margin_percent 
    }: {
      product_id: string;
      price_book_id: string;
      unit_price: number;
      cost?: number;
      margin_percent?: number;
    }) => {
      // First check if price exists
      const { data: existing } = await supabase
        .from("product_prices")
        .select("id")
        .eq("product_id", product_id)
        .eq("price_book_id", price_book_id)
        .single();

      if (existing) {
        // Update existing price
        const { data, error } = await supabase
          .from("product_prices")
          .update({ unit_price, cost, margin_percent, last_updated: new Date().toISOString() })
          .eq("id", existing.id)
          .select()
          .single();
        
        if (error) throw error;
        return data;
      } else {
        // Insert new price
        const { data, error } = await supabase
          .from("product_prices")
          .insert({ product_id, price_book_id, unit_price, cost, margin_percent })
          .select()
          .single();
        
        if (error) throw error;
        return data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["product_prices"] });
      toast({
        title: "Success",
        description: "Price updated successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to update price: ${error.message}`,
        variant: "destructive",
      });
    },
  });

  const bulkUpdatePrices = useMutation({
    mutationFn: async (prices: ProductPriceInsert[]) => {
      const { data, error } = await supabase
        .from("product_prices")
        .upsert(prices, { onConflict: "product_id,price_book_id" })
        .select();
      
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["product_prices"] });
      toast({
        title: "Success",
        description: `${data?.length || 0} prices updated successfully`,
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to update prices: ${error.message}`,
        variant: "destructive",
      });
    },
  });

  return {
    priceBooks,
    productPrices,
    isLoading: priceBooksLoading || pricesLoading,
    updatePrice,
    bulkUpdatePrices,
  };
};
